import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  Stack,
  LinearProgress,
  Divider,
  alpha
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  EmojiEvents,
  TrendingUp,
  Insights,
  Percent,
  ArrowForward,
  CheckCircle
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import featuredService from '../../services/featuredService';

// A csomagok megjelenített nevei — ugyanaz, mint a /reszletek árazásában
const TIER_LABELS = { TOP: 'Kiemelés prémium', TIER2: 'Kiemelés', TIER3: 'Előfizetés' };

const FeaturedPromoCard = ({ providerId, categoryId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [tierLimits, setTierLimits] = useState([]);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFeaturedData();
  }, [providerId, categoryId]);

  const loadFeaturedData = async () => {
    if (!categoryId) return;

    try {
      setLoading(true);
      
      const [tiersResponse, activeSubResponse] = await Promise.all([
        featuredService.getCategoryTiers(categoryId),
        featuredService.getMyActiveSubscription()
      ]);

      if (tiersResponse.success) {
        setTierLimits(tiersResponse.data);
      }

      if (activeSubResponse.success && activeSubResponse.data) {
        setActiveSubscription(activeSubResponse.data);
      }
    } catch (error) {
      console.error('Error loading featured data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateToFeatured = () => {
    navigate('/provider/featured');
  };

  // AKTÍV ELŐFIZETÉS WIDGET
  if (activeSubscription) {
    const DAY_MS = 1000 * 60 * 60 * 24;
    const daysRemaining = Math.max(0, Math.ceil((new Date(activeSubscription.endsAt) - new Date()) / DAY_MS));
    // A teljes időszak a tényleges kezdet–lejárat alapján (admin által, MANUAL módon adott kiemelésnél is helyes)
    const totalDays = activeSubscription.startsAt
      ? Math.max(1, Math.round((new Date(activeSubscription.endsAt) - new Date(activeSubscription.startsAt)) / DAY_MS))
      : 30;
    const progress = Math.min(100, Math.max(0, ((totalDays - daysRemaining) / totalDays) * 100));

    return (
      <Card
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.15)} 100%)`,
          border: `2px solid ${theme.palette.secondary.main}`,
          borderRadius: '16px',
          boxShadow: `0 4px 20px ${alpha(theme.palette.secondary.main, 0.2)}`
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <EmojiEvents sx={{ fontSize: 40, color: theme.palette.secondary.main, mr: 1.5 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Kiemelt Státusz
              </Typography>
              <Chip
                label={TIER_LABELS[activeSubscription.tier] || activeSubscription.tier}
                size="small"
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                  color: theme.palette.text.primary,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  mt: 0.5
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={1.5}>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Hátralévő idő
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {daysRemaining} nap
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: theme.palette.grey[200],
                  '& .MuiLinearProgress-bar': {
                    background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                    borderRadius: 4
                  }
                }}
              />
            </Box>

            <Typography variant="caption" color="text.secondary">
              Lejárat: {new Date(activeSubscription.endsAt).toLocaleDateString('hu-HU')}
            </Typography>

            {activeSubscription.autoRenew && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckCircle sx={{ fontSize: 16, color: theme.palette.success.main }} />
                <Typography variant="caption" color="success.main">
                  Automatikus megújítás aktív
                </Typography>
              </Box>
            )}
          </Stack>

          <Button
            fullWidth
            variant="outlined"
            endIcon={<ArrowForward />}
            onClick={handleNavigateToFeatured}
            sx={{
              mt: 2,
              borderColor: theme.palette.secondary.main,
              color: theme.palette.secondary.main,
              '&:hover': {
                borderColor: theme.palette.secondary.dark,
                backgroundColor: alpha(theme.palette.secondary.main, 0.1)
              }
            }}
          >
            Előfizetés Kezelése
          </Button>
        </CardContent>
      </Card>
    );
  }

  // PROMÓ WIDGET (nincs előfizetés)
  return (
    <Card
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.background.paper} 100%)`,
        border: `2px solid ${theme.palette.secondary.main}`,
        borderRadius: '16px',
        boxShadow: `0 4px 20px ${alpha(theme.palette.secondary.main, 0.15)}`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '150px',
          height: '150px',
          background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.2)} 0%, transparent 70%)`,
          borderRadius: '50%',
          transform: 'translate(30%, -30%)'
        }
      }}
    >
      <CardContent sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <EmojiEvents sx={{ fontSize: 40, color: theme.palette.secondary.main, mr: 1.5 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Légy Kiemelt!
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Növeld a láthatóságod
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={1} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUp sx={{ fontSize: 20, color: theme.palette.success.main }} />
            <Typography variant="body2">
              <strong>Előrébb</strong> a keresési találatokban
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Insights sx={{ fontSize: 20, color: theme.palette.info.main }} />
            <Typography variant="body2">
              <strong>Részletes statisztikák</strong> a profilod látogatásairól
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Percent sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
            <Typography variant="body2">
              <strong>Nincs jutalék</strong> a foglalások után
            </Typography>
          </Box>
        </Stack>

        {!loading && tierLimits.length > 0 && (
          <Box sx={{ mb: 2, p: 1.5, backgroundColor: alpha(theme.palette.secondary.main, 0.1), borderRadius: '8px' }}>
            <Typography variant="caption" color="text.secondary">
              Már
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
              {Math.min(...tierLimits.map(t => t.monthlyPrice)).toLocaleString()} Ft
              <Typography component="span" variant="caption" color="text.secondary">
                /hó
              </Typography>
            </Typography>
          </Box>
        )}

        <Button
          fullWidth
          variant="contained"
          size="large"
          endIcon={<ArrowForward />}
          onClick={handleNavigateToFeatured}
          sx={{
            background: `linear-gradient(45deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
            color: theme.palette.text.primary,
            fontWeight: 600,
            py: 1.5,
            boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.3)}`,
            '&:hover': {
              background: `linear-gradient(45deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
              boxShadow: `0 6px 16px ${alpha(theme.palette.secondary.main, 0.4)}`,
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          Csomag Választás
        </Button>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 1 }}
        >
          Bármikor lemondható ·{' '}
          <Box component={RouterLink} to="/reszletek#arazas" sx={{ color: 'inherit', fontWeight: 600 }}>
            Csomagok összehasonlítása
          </Box>
        </Typography>
      </CardContent>
    </Card>
  );
};

export default FeaturedPromoCard;